import React from 'react';
import PropTypes from 'prop-types';

import { ElementClickTracker } from '../../../analytics/cohesion';
import { ELEMENT_TYPES } from '../../../analytics/cohesion/events/element-clicked/index';
import Button from '../../Shared/Button';

/**
 * @typedef {import('../../../types/provider').Provider} Provider
 */

/**
 * Extract address parts from provider for display.
 * @param {Provider} provider
 */
function getOfficeAddressParts(provider) {
  const streetAddress = provider?.office?.addressLine?.trim() || '';
  const cityState = provider?.office?.cityState?.trim() || '';
  const hasOfficeData = Boolean(streetAddress && cityState);

  const fallback = (provider?.address || '').trim();

  return {
    hasOfficeData,
    streetAddress,
    cityState,
    singleLineAddress: hasOfficeData
      ? `${streetAddress} ${cityState}`
      : fallback,
  };
}

/**
 * Renders a single provider search result with optional blocker message or Add button.
 *
 * This component is purely presentational - it receives all data via props
 * and has no internal validation logic.
 *
 * @param {{
 *   provider: Provider,
 *   blockerMessage: string | null,
 *   canAdd: boolean,
 *   onAdd: () => void,
 *   disabled?: boolean,
 * }} props
 */
const ProviderSearchResult = ({
  provider,
  blockerMessage = null,
  canAdd = false,
  onAdd,
  disabled = false,
}) => {
  if (!provider) return null;

  const { providerImageUrl, displayName, primarySpecialty } = provider;

  const { hasOfficeData, streetAddress, cityState, singleLineAddress } =
    getOfficeAddressParts(provider);

  return (
    <div className="w-full flex flex-col">
      <div
        className="w-full flex flex-col lg:flex-row lg:items-center p-3 gap-6 rounded-lg border border-gray-20"
        data-testid="provider-search-result"
      >
        {/* Provider info section */}
        <div
          className="flex items-center gap-3 flex-1 min-w-fit"
          data-testid="provider-details"
        >
          {providerImageUrl ? (
            <img
              src={providerImageUrl}
              alt={displayName}
              className="w-9 h-12"
              loading="lazy"
              width={36}
              height={48}
            />
          ) : (
            <div
              aria-hidden="true"
              className="w-9 h-12 bg-gray-200 rounded-sm"
            />
          )}

          <div className="flex-1">
            <div className="flex flex-col lg:flex-row lg:items-center gap-1">
              <span className="text-lg font-bold">{displayName}</span>
              {primarySpecialty?.specialtyName && (
                <>
                  <span
                    className="text-base font-thin px-1 hidden lg:inline"
                    aria-hidden="true"
                  >
                    •
                  </span>
                  <span className="text-base font-thin">
                    {primarySpecialty?.specialtyName}
                  </span>
                </>
              )}
            </div>

            <address className="not-italic text-base font-thin">
              {hasOfficeData ? (
                <>
                  <div className="lg:hidden">{streetAddress}</div>
                  <div className="lg:hidden">{cityState}</div>
                  <div className="hidden lg:block">{singleLineAddress}</div>
                </>
              ) : (
                <div>{singleLineAddress}</div>
              )}
            </address>
          </div>
        </div>

        {/* Blocker message or Add button */}
        {blockerMessage ? (
          <div
            className="w-full px-4 py-3 rounded bg-coral-05 text-coral-96 text-sm font-semibold lg:max-w-fit"
            role="alert"
            aria-live="polite"
            aria-atomic="true"
            data-testid="blocker-message"
          >
            {blockerMessage}
          </div>
        ) : (
          <ElementClickTracker
            actionOutcome="Add Provider"
            webElement={{
              elementType: ELEMENT_TYPES.BUTTON,
              location: 'Elevated Portal Roster',
              name: 'add provider to roster',
            }}
          >
            <Button
              disabled={!canAdd || disabled}
              onClick={onAdd}
              className="w-full lg:w-25"
              variant="outline"
              theme="deep-blue"
              aria-disabled={!canAdd || disabled || undefined}
              data-qa-target="add-provider-to-roster-button"
            >
              Add
            </Button>
          </ElementClickTracker>
        )}
      </div>
    </div>
  );
};

ProviderSearchResult.propTypes = {
  provider: PropTypes.shape({
    providerCode: PropTypes.string,
    providerImageUrl: PropTypes.string,
    displayName: PropTypes.string,
    primarySpecialty: PropTypes.shape({
      specialtyName: PropTypes.string,
    }),
    office: PropTypes.shape({
      addressLine: PropTypes.string,
      cityState: PropTypes.string,
    }),
    address: PropTypes.string,
  }),
  blockerMessage: PropTypes.string,
  canAdd: PropTypes.bool,
  onAdd: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default ProviderSearchResult;
